/* /book/:slug/:handle — FAITHARAMA FLIPBOOK VIEWER
   Public route, no auth.  Embeds Mike's Heyzine flipbook in an iframe and
   stamps a Faitharama-branded footer below it that links to the referring
   member's mirror site.  One master flipbook, dynamic footer per viewer. */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const WINE = "#8B2A2A";

export default function FlipbookViewer() {
  const { slug, handle } = useParams();
  const [book, setBook] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Faitharama Flipbook";
    // 1. Save referral handle so any subsequent /signup credits this member.
    if (handle) {
      try { window.localStorage.setItem("aff", handle); } catch (_) {}
    }
    // 2. Log the view for the member's flipbook stats dashboard.
    if (slug) {
      axios.post(`${BACKEND}/api/flipbook/view`, { slug, handle: handle || "" })
        .catch(() => { /* tracking is best-effort */ });
    }
    // 3. Fetch the flipbook payload.
    axios.get(`${BACKEND}/api/heyzine/flipbooks`)
      .then((r) => {
        const list = r.data?.flipbooks || [];
        const want = (slug || "").toLowerCase();
        const found = list.find((b) => {
          const url = (b.embed_url || "").toLowerCase();
          // Match by short id at the end of /flip-book/{slug}.html
          return url.includes(`/flip-book/${want}.html`) || url.includes(`/${want}.html`) || b.id?.toLowerCase().startsWith(want);
        });
        if (!found) { setError("That flipbook is not on the shelf."); return; }
        setBook(found);
      })
      .catch(() => setError("Could not load the flipbook. Try again in a moment."));
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="flipbook-viewer">
      {/* THIN BRAND BAR */}
      <div className="px-4 py-2.5 text-center text-[11px] tracking-[0.32em] uppercase font-bold border-b"
           style={{ background: "#0F172A", color: ORANGE, borderColor: ORANGE }}>
        ★ FAITHARAMA FLIPBOOK VIEWER ★
      </div>

      {/* FLIPBOOK FRAME */}
      <div className="flex-1 relative bg-black min-h-[60vh]">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 px-4 text-center">
            <div>
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50"/>
              <div className="text-lg">{error}</div>
              <Link to="/library" className="inline-block mt-4 text-sm underline" style={{ color: ORANGE }}>
                Go to the Library →
              </Link>
            </div>
          </div>
        ) : !book ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">Loading…</div>
        ) : book.embed_url ? (
          <iframe src={book.embed_url}
                  title={book.title || "Flipbook"}
                  className="absolute inset-0 w-full h-full"
                  allow="fullscreen"
                  style={{ border: 0 }}/>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
            This book doesn&apos;t have a readable link yet.
          </div>
        )}
      </div>

      {/* FOOTER — branded, links to the referring member's mirror site */}
      <div className="px-4 py-4 border-t-2 flex flex-wrap items-center justify-between gap-3"
           style={{ background: "#EAF6FC", borderColor: ORANGE }}
           data-testid="flipbook-viewer-footer">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: WINE }}>
            ★ BROUGHT TO YOU BY
          </div>
          <div className="font-serif-display text-xl md:text-2xl uppercase leading-tight" style={{ color: "#0F172A" }}>
            Faitharama.com
          </div>
        </div>
        <Link to={handle ? `/m/${handle}` : "/"}
              data-testid="flipbook-viewer-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm md:text-base font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition"
              style={{ background: GREEN }}>
          Visit Faitharama.com <ExternalLink className="w-4 h-4"/>
        </Link>
      </div>
    </div>
  );
}
